import {useDispatch, useSelector, shallowEqual} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {wrapper} from '@jobseeker/common-core/stores';
import {metaInfo} from '@configs';
import {JOBFEED, MYPROFILE, CATEGORY} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {recommendationService} from '@jobseeker/common-core/services';
import {SeoLayout} from '@layouts';
import {getConfigByApp, utilServerSideDeviceDetection} from '@utils';
import type {GetServerSidePropsContext} from 'next/types';
import type {AppState} from '@jobseeker/common-core/types';
import {Box, Button, FormControl, Input, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import {Image} from '@jobseeker/lotus/components/Image';
import {profileService, userService} from '@jobseeker/common-core/services';
import {RegistrationTypes} from '../../../types/index';
import {keyframes} from '@mui/material';

import {openUrl} from '@jobseeker/common-core/utils/common';
import {
  CoFields,
  EdFields,
  WeFields,
  PdFields,
  AiFields,
  Recommendation,
} from '@jobseeker/common-core/services/types';

type MyProfileProps = {
  seoConfig: object;
  metaData: object;
};
import {userProfile} from '@jobseeker/common-core/helpers';
import {
  ProfileSection,
  MiddleProfileSection,
  RecommendationSection,
} from '@components/MyProfile';

const ABS_URL = 'https://static.iimjobs.com/';

const MyProfilePage = ({seoConfig, metaData}: MyProfileProps) => {
  // const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBFEED);

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch, true);
  const {isLoggedIn} = useSelector((state: AppState) => state.auth);
  const {userId} = useSelector((state: AppState) => state.auth);
  const userData = useSelector((state: AppState) => state.user, shallowEqual);
  const profileSchema = useSelector(
    (state: AppState) => state.user?.profile?.schema
  );

  useEffect(() => {
    if (!userData?.profile?.email) {
      userProfile('profile?apply=true', dispatch);
    }
  }, [isLoggedIn, userData]);

  const [schema, setSchema] = useState<{[key: string]: string}>({});

  const [personalInfo, setPersonalInfo] = useState<PdFields>();
  const [educationalDetails, setEducationalDetails] = useState<EdFields[]>([]);
  const [courseDetails, setCourseDetails] = useState<CoFields[]>([]);
  const [workExperience, setWorkExperience] = useState<WeFields[]>([]);
  const [profileInfo, setProfileInfo] = useState<any>();
  const [additionalDetails, setAdditionalDetails] = useState<AiFields>();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [popUpRecommendation, setPopUpRecommendation] =
    useState<Recommendation | null>(null);

  const [socialModel, setSocialModel] = useState<{
    type: 'Twitter' | 'Facebook' | 'LinkedIn' | '';
    url: string;
  }>({type: '', url: ''});

  const [socialUrlError, setSocialUrlError] = useState<string>('');
  const [processRequestMsgType, setProcessRequestMsgType] = useState<number>(0);

  useEffect(() => {
    if (profileSchema) {
      setSchema({...profileSchema});
    }
  }, [profileSchema]);

  const fetchUserInfo = async () => {
    const data = await profileService.getProfile({
      pd: true,
      we: true,
      ed: true,
      co: true,
      ai: true,
    });
    if (data.ed) {
      setEducationalDetails(data.ed);
    }
    if (data.co) {
      setCourseDetails(data.co);
    }
    if (data.pd) {
      setPersonalInfo(data.pd);
    }
    if (data.we) {
      setWorkExperience(data.we);
    }
    if (data.ai) {
      setAdditionalDetails(data.ai);
    }
  };

  const getProfileData = async () => {
    const data = await userService.profile('profile');
    if (data.profile) {
      setProfileInfo(data.profile);
    }
  };

  const getRecommendations = async () => {
    const res = await recommendationService.getRecommendations();
    setRecommendations(res?.data || []);
  };

  const handleSocialClick = (
    type: 'Twitter' | 'Facebook' | 'LinkedIn',
    url: string
  ) => {
    setSocialModel({type, url});
  };

  const sendRecommendationRequest = async (email: string) => {
    const res = await recommendationService.sendRecommendationRequest(
      email,
      'send'
    );

    if (res?.error) {
      if (
        res?.error?.message === 'You cannot recommend yourself.' ||
        res?.error?.message === 'Request already sent.' ||
        res?.error?.message ===
          'You have exceeded the maximum number of recommendation requests allowed per day. Please try again tomorrow.'
      )
        return res?.error?.message;
      return '';
    } else {
      await getRecommendations();
      return res;
    }
  };

  const approveRecommendation = async (
    recommendationId: number,
    action: string
  ) => {
    const res = await recommendationService.approveRecommendation(
      recommendationId,
      action
    );
    // console.log(res);
    if (res?.error) {
      setProcessRequestMsgType(1);
      return 1;
    } else {
      setProcessRequestMsgType(2);
      return 2;
    }
  };

  const rejectRecommendation = async (
    recommendationId: number,
    action: string
  ) => {
    const res = await recommendationService.rejectRecommendation(
      recommendationId,
      action
    );
    if (res?.error) {
      setProcessRequestMsgType(1);
      return 1;
    } else {
      setProcessRequestMsgType(2);
      return 2;
    }
  };

  useEffect(() => {
    if (userId !== '-1' && isLoggedIn) {
      fetchUserInfo();
      getProfileData();
      getRecommendations();
    }
  }, [userId, isLoggedIn]);

  const handleSocialUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSocialModel({type: socialModel.type, url: e.target.value});
    if (e.target.value === '') setSocialUrlError('Please enter url');
    else setSocialUrlError('');
  };

  const handleUpdateSocialUrl = async () => {
    const profileUpdationParams: AiFields = {};
    let infoString = '';
    const requiredString = '';
    if (additionalDetails?.id) {
      profileUpdationParams.id = additionalDetails?.id;
    }
    if (socialModel.type === 'LinkedIn') {
      if (additionalDetails?.linkedin !== socialModel.url) {
        profileUpdationParams.linkedin = socialModel.url;
        infoString += schema['ai.linkedin'] ?? '';
      }
    }
    if (socialModel.type === 'Facebook') {
      if (additionalDetails?.facebook !== socialModel.url) {
        profileUpdationParams.facebook = socialModel.url;
        infoString += schema['ai.facebook'] ?? '';
      }
    }
    if (socialModel.type === 'Twitter') {
      if (additionalDetails?.twitter !== socialModel.url) {
        profileUpdationParams.twitter = socialModel.url;
        infoString += schema['ai.twitter'] ?? '';
      }
    }
    if (infoString?.length > 1) {
      setLoading(true);
      const updationResponse = await profileService.updateProfile({
        ai: {infoString, requiredString, ...profileUpdationParams},
      });
      if (updationResponse?.error) {
        if (updationResponse?.error?.code !== 0) {
          setLoading(false);
          setSocialUrlError(updationResponse?.error?.message || '');
        }
        // setSocialModel({type: '', url: ''});
        return;
      }
      openUrl('/myprofile', '_self');
    }
    setSocialModel({type: '', url: ''});
  };

  const popupFromLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(100%);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;
  const overlayFromTopLeft = keyframes`
  0% {
    opacity: 0;
    transform: translate(-100%, -100%);
  }
  100% {
    opacity: 1;
    transform: translate(0, 0);
  }
  `;

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={false} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: {xxs: 'column', md: 'row'},
            width: '100%',
            minHeight: '140vh',
            float: 'left',
          }}
        >
          <Box
            sx={{
              width: {xxs: '100%', md: '24%'},
              float: 'left',
              minWidth: '200px',
              bgcolor: {xxs: '#f7f7f7', md: ''},
            }}
          >
            <ProfileSection
              profileInfo={profileInfo}
              onSocialClick={handleSocialClick}
            />
          </Box>
          <Box
            sx={{
              float: 'left',
              width: {xxs: '100%', md: '76%'},
              minHeight: '635px',
              display: 'flex',
              flexDirection: {xxs: 'column', md: 'row'},
              bgcolor: '#fff',
            }}
          >
            <Box
              sx={{
                width: {xxs: '100%', md: '70%'},
                borderRight: {xxs: 'none', md: '5px solid #f7f7f7'},
              }}
            >
              <MiddleProfileSection
                educationalDetails={educationalDetails}
                certificationDetails={courseDetails}
                professionalDetails={workExperience}
                moreDetails={personalInfo}
                recommendations={recommendations}
                handleApproveRecommendation={approveRecommendation}
                handleRejectRecommendation={rejectRecommendation}
              />
            </Box>
            <Box sx={{width: {xxs: '100%', md: '30%'}}}>
              <RecommendationSection
                recommendations={recommendations}
                handleSendRecommendationRequest={sendRecommendationRequest}
                setPopUpRecommendation={setPopUpRecommendation}
              />
            </Box>
          </Box>
        </Box>

        {socialModel.type !== '' && (
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              bgcolor: 'rgb(0,0,0,0.4)',
              zIndex: 20,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                margin: 'auto',
                width: {xxs: '95%', xs: '90%', sm: '85%', md: '55%'},
                bgcolor: '#fff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                zIndex: 30,
              }}
            >
              <Box sx={{p: '20px 20px 40px', bgcolor: '#f8f9fa'}}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    p: '15px 20px',
                    cursor: 'pointer',
                    zIndex: 100,
                  }}
                  onClick={() => {
                    setSocialModel({type: '', url: ''});
                    setSocialUrlError('');
                  }}
                >
                  <span>
                    <Image
                      src={`${ABS_URL}resources/images/close-icon.png`}
                      alt="close"
                      width={12}
                      height={12}
                    />
                  </span>
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px !important',
                    fontWeight: 700,
                    width: '98%',
                    m: '0 auto',
                    mb: '15px',
                    float: 'none !important',
                  }}
                >
                  Connect
                  <span> {socialModel.type}</span>
                </Typography>
                <FormControl
                  sx={{width: '98%', m: 'auto', p: 0, display: 'block'}}
                >
                  <Box sx={{width: '100%', m: '0 auto', mb: '5px'}}>
                    <Typography
                      sx={{fontSize: '14px !important', m: '0 0 10px'}}
                    >
                      Paste the public profile URL of your
                      <span> {socialModel.type}</span>
                      account.
                    </Typography>
                    <Typography
                      sx={{fontSize: '14px !important', m: '0 0 10px'}}
                    >
                      You can find the link in the profile sharing options of
                      your
                      <span> {socialModel.type}</span>
                      Profile.
                    </Typography>
                    <Input
                      placeholder={`For Example: https://www.${
                        socialModel.type.charAt(0).toLowerCase() +
                        socialModel.type.slice(1)
                      }.com/in/yourname`}
                      sx={{
                        p: '10px 15px',
                        width: '100%',
                        border: 'none',
                        borderBottom: '1px solid #e6e6e6',
                        borderRadius: 0,
                        bgcolor: '#fff',
                        fontSize: 13,
                        lineHeight: 'inherit',
                        '&:before': {
                          borderBottom: '1px solid #e6e6e6',
                        },
                        '&:after': {
                          borderBottom: '1px solid #e6e6e6',
                        },
                        '&:hover:not(.Mui-disabled, .Mui-error):before': {
                          borderBottom: '1px solid #e6e6e6 !important',
                        },
                      }}
                      value={socialModel.url}
                      onChange={handleSocialUrlChange}
                    />
                  </Box>
                  {socialUrlError !== '' && (
                    <Typography
                      component="span"
                      sx={{
                        display: 'inline-block',
                        fontSize: 12,
                        lineHeight: '13px',
                        color: 'indianred',
                        width: '98%',
                        m: '0px 0px 0px 21px',
                      }}
                    >
                      {socialUrlError}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      clear: 'both',
                      overflow: 'hidden',
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      mb: '10px',
                      float: 'right',
                      bgcolor: '#f8f9fa',
                    }}
                  >
                    <Button
                      sx={{
                        float: 'right',
                        borderRadius: '0 !important',
                        fontSize: 14,
                        p: '8px 20px',
                        mr: '5px',
                        lineHeight: 'normal',
                        width: '90px',
                        bgcolor: '#9b9b9b',
                        color: '#fff',
                        border: 0,
                        '&:hover': {bgcolor: '#9b9b9b', color: '#fff'},
                      }}
                      onClick={handleUpdateSocialUrl}
                    >
                      {loading ? 'Loading...' : 'Done'}
                    </Button>
                  </Box>
                </FormControl>
              </Box>
            </Box>
          </Box>
        )}
        {popUpRecommendation && (
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              bgcolor: 'rgb(0,0,0,0.4)',
              zIndex: 20,
              animation: `${overlayFromTopLeft} 0.4s ease-out`,
            }}
          >
            <Box
              sx={{
                width: {xxs: 'auto', md: '580px'},
                py: {xxs: '10px', md: '30px'},
                mx: {xxs: '10px', md: 'auto'},
                animation: `${popupFromLeft} 0.4s ease-in-out`,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: '#fff',
                  border: '1px solid rgb(0,0,0,0.2)',
                  borderRadius: '6px',
                  outline: 'none',
                  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.5)',
                }}
              >
                <Box
                  sx={{
                    minHeight: '16.42px',
                    p: '15px',
                    borderBottom: '1px solid #e5e5e5',
                  }}
                >
                  <Button
                    sx={{
                      mt: '-2px',
                      color: '#999',
                      float: 'right',
                      fontSize: '21px',
                      lineHeight: 0.5,
                      minWidth: 0,
                      p: 0,
                    }}
                    onClick={() => setPopUpRecommendation(null)}
                  >
                    x
                  </Button>
                  <Typography
                    sx={{
                      m: 0,
                      lineHeight: 1.42,
                      fontSize: '18px',
                      fontWeight: 500,
                      color: '#4b4b4b',
                    }}
                  >
                    {popUpRecommendation?.status === 0
                      ? 'Pending'
                      : popUpRecommendation?.status === 1
                      ? 'Approve'
                      : 'Rejected'}{' '}
                    Recommendation
                    {popUpRecommendation?.status === 3 && (
                      <Typography
                        component="span"
                        sx={{
                          bgcolor: '#d94242',
                          color: '#fff',
                          border: '1px solid #b92929',
                          borderRadius: '3px',
                          p: '2px 8px',
                          m: '0px 0px 0px 10px',
                          fontSize: 10,
                        }}
                      >
                        Rejected
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <Box sx={{p: '20px', overflow: 'hidden'}}>
                  {processRequestMsgType === 1 && (
                    <Typography
                      sx={{
                        color: '#fff',
                        bgcolor: '#b86363',
                        p: '5px 17px',
                        fontSize: 13,
                        mb: '20px',
                        borderRadius: '4px',
                      }}
                    >
                      Some error occured, Pleae try again.
                      <Typography
                        component={'span'}
                        sx={{
                          fontSize: 21,
                          lineHeight: 1,
                          float: 'right',
                          cursor: 'pointer',
                          fontWeight: 400,
                        }}
                        onClick={() => setProcessRequestMsgType(0)}
                      >
                        x
                      </Typography>
                    </Typography>
                  )}
                  {processRequestMsgType === 2 && (
                    <Typography
                      sx={{
                        color: '#fff',
                        bgcolor: '#996',
                        p: '5px 17px',
                        fontSize: 13,
                        mb: '20px',
                        borderRadius: '4px',
                      }}
                    >
                      Action performed successfully.
                      <Typography
                        component={'span'}
                        sx={{
                          fontSize: 21,
                          lineHeight: 1,
                          float: 'right',
                          cursor: 'pointer',
                          fontWeight: 400,
                        }}
                        onClick={() => setProcessRequestMsgType(0)}
                      >
                        x
                      </Typography>
                    </Typography>
                  )}

                  {popUpRecommendation?.status === 1 ? (
                    <Typography
                      sx={{
                        color: '#4b4b4b',
                        fontSize: '14px',
                        wordBreak: 'break-word',
                        WordBreak: 'break-word',
                        bgcolor: '#eee',
                        minHeight: '80px',
                        border: '1px solid #ccc',
                        p: '10px',
                      }}
                    >
                      {popUpRecommendation?.recommendationText}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        color: '#4b4b4b',
                        fontSize: '14px',
                        wordBreak: 'break-word',
                      }}
                    >
                      {popUpRecommendation?.status === 0
                        ? 'No response received yet.'
                        : popUpRecommendation?.recommendationText}
                    </Typography>
                  )}

                  {popUpRecommendation?.status !== 0 && (
                    <Box
                      sx={{
                        mt: '20px',
                        textAlign: 'right',
                      }}
                    >
                      {popUpRecommendation?.status === 1 && (
                        <>
                          <Button
                            sx={{
                              borderRadius: 0,
                              color: '#4b4b4b',
                              fontSize: 14,
                              p: '6px 12px',
                              mr: '5px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              border: '1px solid #ccc',
                              textShadow: '0 1px 0 #fff',
                            }}
                            onClick={() =>
                              approveRecommendation(
                                popUpRecommendation?.id,
                                'approve'
                              )
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            sx={{
                              borderRadius: 0,
                              color: '#4b4b4b',
                              fontSize: 14,
                              p: '6px 12px',
                              mr: '5px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              border: '1px solid #ccc',
                              textShadow: '0 1px 0 #fff',
                            }}
                            onClick={() =>
                              rejectRecommendation(
                                popUpRecommendation?.id,
                                'reject'
                              )
                            }
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      <Button
                        sx={{
                          borderRadius: 0,
                          color: '#4b4b4b',
                          fontSize: 14,
                          p: '6px 12px',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          border: '1px solid #ccc',
                          textShadow: '0 1px 0 #fff',
                        }}
                        onClick={() => setPopUpRecommendation(null)}
                      >
                        Close
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Interceptor>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const metaData = getConfigByApp(metaInfo, JOBFEED, true, CATEGORY);
    const slugUrl = context?.resolvedUrl || '';

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: MYPROFILE},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    try {
      if (process.env.NEXT_PUBLIC_PROFILE_SCHEMA) {
        const response = await fetch(process.env.NEXT_PUBLIC_PROFILE_SCHEMA);
        const profileSchema: RegistrationTypes.SchemaType =
          await response.json();

        const schema: {[key: string]: string} = {};
        if (profileSchema) {
          Object.keys(profileSchema)?.map((sectionName: string) => {
            Object.keys(profileSchema[sectionName])?.map((fieldKey: string) => {
              schema[
                `${sectionName}.${profileSchema[sectionName][fieldKey]['field']}`
              ] = fieldKey;
            });
          });
        }
        store.dispatch({
          type: 'User/set_profile_schema',
          payload: schema,
        });
      }
    } catch (err) {}

    const seoConfig = await loadSEO(
      MYPROFILE,
      {
        title: '',
        description: '',
      },
      slugUrl,
      true
    );
    return {
      props: {
        isMobile,
        seoConfig,
        metaData,
      },
    };
  }
);

export default MyProfilePage;
